import { useCallback, useEffect, useRef, useState } from "react";
import { fetchWrapper } from "services/api-client/fetchWrapper";
import { captureException } from "scripts/logging/error-report";

/**
 * HAR.com's display label for every listing filter, keyed by the filter name
 * used on the wire — e.g. `streetaddress` -> `"Listing Address"`.
 *
 * Sourced from HAR.com's search form, so it covers the "extra" (passthrough)
 * filters that have no field on the search tool input and therefore no label
 * anywhere else in the app.
 */
export type ListingFilterLabels = Record<string, string>;

export interface IUseListingFilterLabelsOptions {
  fetchOnMount?: boolean;
}

export interface IUseListingFilterLabels {
  /** Always an object; `{}` until loaded, and `{}` if the load failed. */
  labels: ListingFilterLabels;
  /** Label for a filter name, falling back to the raw name. */
  labelFor: (name: string) => string;
  isLoading: boolean;
  error: Error | null;
  refresh: () => void;
}

interface ICachedLabels {
  data: ListingFilterLabels;
  fetchedAt: number;
  expiresAt: number;
}

const ENDPOINT = "/api/v1/chatbot/listing/filter-labels";
const STORAGE_KEY = "har_listing_filter_labels_cache";
// Matches SEARCH_FORM_CACHE_SECONDS on the backend.
const CACHE_EXPIRY_TIME = 60 * 60 * 1000; // 1 hour

// The mapping is a singleton: several components can mount at once (Filters +
// FilterPills), and StrictMode double-mounts in dev. A module-level cache plus
// an in-flight promise keeps that to a single request.
let memoryCache: ICachedLabels | null = null;
let inFlight: Promise<ListingFilterLabels> | null = null;

const isFresh = (cached: ICachedLabels | null): cached is ICachedLabels =>
  !!cached && Date.now() < cached.expiresAt;

const readLabelCache = (): ICachedLabels | null => {
  try {
    if (typeof Storage === "undefined") return null;

    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed: ICachedLabels = JSON.parse(raw);
    if (
      !parsed?.data ||
      typeof parsed.data !== "object" ||
      typeof parsed.expiresAt !== "number"
    ) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    if (!isFresh(parsed)) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return parsed;
  } catch (error) {
    console.warn("[useListingFilterLabels] Failed to read cache:", error);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (removeError) {
      console.warn("[useListingFilterLabels] Failed to clear cache:", removeError);
    }
    return null;
  }
};

const writeLabelCache = (data: ListingFilterLabels): ICachedLabels => {
  const fetchedAt = Date.now();
  const entry: ICachedLabels = {
    data,
    fetchedAt,
    expiresAt: fetchedAt + CACHE_EXPIRY_TIME,
  };
  memoryCache = entry;

  try {
    if (typeof Storage !== "undefined") {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
    }
  } catch (error) {
    console.warn("[useListingFilterLabels] Failed to cache labels:", error);
  }

  return entry;
};

/** Drop every cached copy so the next load refetches. Exported for tests. */
export const clearListingFilterLabelsCache = (): void => {
  memoryCache = null;
  inFlight = null;
  try {
    if (typeof Storage !== "undefined") {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch (error) {
    console.warn("[useListingFilterLabels] Failed to clear cache:", error);
  }
};

/** Keep only usable `string -> non-empty string` pairs. */
const sanitize = (data: unknown): ListingFilterLabels =>
  Object.fromEntries(
    Object.entries((data ?? {}) as Record<string, unknown>).filter(
      ([name, label]) => !!name && typeof label === "string" && !!label
    )
  ) as ListingFilterLabels;

// Deliberately takes no AbortSignal. The promise is shared by every mounted
// caller, so aborting it on one caller's unmount kills the request for all of
// them — and under StrictMode the very first mount unmounts immediately, which
// aborted the fetch before it left the browser. Callers cancel by ignoring the
// result instead; the response is cached, so nothing is wasted.
async function loadLabels(): Promise<ListingFilterLabels> {
  if (isFresh(memoryCache)) return memoryCache.data;
  if (inFlight) return inFlight;

  const stored = readLabelCache();
  if (stored) {
    memoryCache = stored;
    return stored.data;
  }

  inFlight = (async () => {
    const json: any = await fetchWrapper.get(ENDPOINT);
    if (!json?.status) {
      throw new Error("Listing filter labels API returned status=false");
    }

    const labels = sanitize(json?.data);

    // An empty mapping is the backend's degradation when HAR.com's form
    // changes shape — don't pin that for an hour on the client.
    if (Object.keys(labels).length > 0) writeLabelCache(labels);

    return labels;
  })();

  try {
    return await inFlight;
  } finally {
    inFlight = null;
  }
}

/**
 * Read HAR.com's listing-filter display labels.
 *
 * Never throws and never blocks the UI: on failure `labels` stays `{}` and
 * `labelFor` returns the raw filter name, which is still more useful than
 * rendering nothing.
 */
export function useListingFilterLabels(
  options: IUseListingFilterLabelsOptions = {}
): IUseListingFilterLabels {
  const { fetchOnMount = true } = options;
  const [labels, setLabels] = useState<ListingFilterLabels>(
    () => (isFresh(memoryCache) ? memoryCache.data : {})
  );
  const [isLoading, setIsLoading] = useState<boolean>(
    fetchOnMount && !isFresh(memoryCache)
  );
  const [error, setError] = useState<Error | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const safeSet = useCallback(<T,>(setter: (v: T) => void, v: T) => {
    if (mounted.current) setter(v);
  }, []);

  useEffect(() => {
    if (!fetchOnMount) return;

    // Not an AbortController: see loadLabels. A superseded run just drops its
    // result rather than cancelling the request the next run is waiting on.
    let cancelled = false;

    (async () => {
      safeSet(setIsLoading, true);
      try {
        const loaded = await loadLabels();
        if (cancelled) return;
        safeSet(setLabels, loaded);
        safeSet(setError, null);
      } catch (e: unknown) {
        if (cancelled) return;

        // fetchWrapper rejects with a plain string, not an Error.
        const err = e instanceof Error ? e : new Error(String(e));
        console.warn("[useListingFilterLabels] Failed to load labels:", err);
        captureException(err);
        safeSet(setError, err);
      } finally {
        if (!cancelled) safeSet(setIsLoading, false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fetchOnMount, reloadToken, safeSet]);

  const labelFor = useCallback(
    (name: string) => {
      // HAR.com's query string uses list-style keys such as
      // `property_class_id[]`; the form names them without the suffix.
      const key = name.endsWith("[]") ? name.slice(0, -2) : name;
      return labels[key] ?? labels[name] ?? name;
    },
    [labels]
  );

  const refresh = useCallback(() => {
    clearListingFilterLabelsCache();
    safeSet(setLabels, {});
    safeSet(setError, null);
    setReloadToken((token) => token + 1);
  }, [safeSet]);

  return { labels, labelFor, isLoading, error, refresh };
}
